<template>
  <div class="mic-selector">
    <el-tooltip
      class="item"
      effect="dark"
      :content="shortcutKeyPrompt"
      placement="top-start"
    >
      <div
        class="mic-btn"
        @click="handleMicClick"
        tabindex="0"
        aria-label="音频"
        @keydown.enter="handleMicClick"
      >
        <img
          v-if="isMicLoading"
          src="@saas/pages/assets/images/live/loading.png"
          alt=""
          class="loading-icon"
        />
        <img
          v-else-if="showCloseIcon"
          class="mic-icon"
          :src="require('@saas/pages/assets/images/live/mic-close.png')"
          alt="音频"
        />
        <img
          v-else-if="selfIsSpeaking"
          class="mic-icon"
          :src="require('@saas/pages/assets/images/live/mic-open.png')"
          alt="音频"
        />
        <img
          v-else
          class="mic-icon"
          :src="require('@saas/pages/assets/images/live/micro.png')"
          alt="音频"
        />

        <span class="mic-label">音频</span>
      </div>
    </el-tooltip>

    <el-popover
      v-model="popoverVisible"
      placement="bottom-start"
      trigger="manual"
      popper-class="mic-popover"
      :visible-arrow="false"
      :append-to-body="true"
      @hide="onHidePopover"
      @show="onShowPopover"
      @after-leave="onAfterLeave"
    >
      <div class="popover-content">
        <div class="group-title">选择麦克风</div>
        <ul class="device-list">
          <li
            v-for="item in microphoneOptions"
            :key="item.deviceId"
            :class="[
              'device-item',
              {
                selected: selectMicDevice.deviceId === item.deviceId,
                disabled: item.disabled,
              },
            ]"
            @click="!item.disabled && handleSelectMic(item)"
            :tabindex="item.disabled ? -1 : 0"
            :aria-label="item.deviceName"
          >
            <span class="checkmark">
              <img
                v-if="selectMicDevice.deviceId === item.deviceId"
                src="@saas/pages/assets/images/live/selected.png"
                alt=""
              />
            </span>
            <div class="label ellipsis">{{ item.deviceName }}</div>
          </li>
        </ul>
        <div class="divider"></div>
        <div class="group-title">选择扬声器</div>
        <ul class="device-list">
          <li
            v-for="item in speakerListOptions"
            :key="item.deviceId"
            :class="[
              'device-item',
              {
                selected: selectSpeakerDevice.deviceId === item.deviceId,
                disabled: item.disabled,
              },
            ]"
            @click="!item.disabled && handleSelectSpeaker(item)"
            :tabindex="item.disabled ? -1 : 0"
            :aria-label="item.deviceName"
          >
            <span class="checkmark">
              <img
                v-if="selectSpeakerDevice.deviceId === item.deviceId"
                src="@saas/pages/assets/images/live/selected.png"
                alt=""
              />
            </span>
            <div class="label ellipsis">{{ item.deviceName }}</div>
          </li>
        </ul>
        <div class="divider"></div>
        <div
          class="bottom-setting"
          @click="handleSetting"
          tabindex="0"
          aria-label="设置"
          @keydown.enter="handleSetting"
        >
          <span class="checkmark"> </span>
          <span class="setting-label">设置</span>
        </div>
      </div>
      <div
        slot="reference"
        class="arrow-btn"
        @click="handleClickMore"
        tabindex="0"
        aria-label="展开音频选项"
      >
        <i
          :class="isScreenShare ? 'el-icon-arrow-down' : 'el-icon-arrow-up'"
          class="arrow-icon"
        ></i>
      </div>
    </el-popover>
  </div>
</template>

<script>
import { mapMutations, mapState } from "vuex";
import handleSelf from "../mixins/handle-self";
import ScreenShare from "../mixins/screen-share";
import { uploadLiveLog } from "@saas/components/src/utils/log";
import { debounce } from "@saas/components/src/utils/debounce-async";
import { EventBus } from "@saas/components/src/utils/event-bus";
export default {
  name: "MicSelector",
  mixins: [handleSelf, ScreenShare],
  data() {
    return {
      popoverVisible: false,
      isMicLoading: false,
      isSelectMicLoading: false,
      isNeedListenVolumeChange: true,
    };
  },
  watch: {
    selfMicVolume: {
      handler(val) {
        clearTimeout(this.speakTimer);
        this.speakTimer = setTimeout(() => {
          this.isSpeaking = false;
        }, 1000);
      },
      immediate: true,
    },
  },
  computed: {
    ...mapState("live", [
      "microphoneList",
      "speakerList",
      "liveService",
      "currentMicDevice",
      "currentSpeakerDevice",
      "userStatus",
      "screenShareStatus",
      "deviceSwtich",
      "audioPermission",
    ]),
    microphoneOptions() {
      return [
        ...this.microphoneList,
        { deviceId: "disabled", deviceName: "禁用" },
      ];
    },
    speakerListOptions() {
      return [
        ...this.speakerList,
        // { deviceId: "disabled", deviceName: "禁用" },
      ];
    },
    shortcutKeyPrompt() {
      const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
      const shortcut = isMac ? "⌘+Shift+D" : "Ctrl+Shift+D";
      if (!this.showCloseIcon) {
        return `关闭麦克风 (${shortcut})`;
      } else {
        return `打开麦克风 (${shortcut})`;
      }
    },
    selectMicDevice() {
      if (
        this.deviceSwtich.microphone === "off" ||
        this.userStatus.is_up_stage !== 1
      ) {
        return { deviceId: "disabled" };
      } else {
        return this.currentMicDevice;
      }
    },
    selectSpeakerDevice() {
      if (this.deviceSwtich.speaker === "off") {
        return { deviceId: "disabled" };
      } else {
        return this.currentSpeakerDevice;
      }
    },
    showCloseIcon() {
      return (
        this.userStatus.is_up_stage !== 1 || this.userStatus.audio_state !== 1
      );
    },
    // 是否在共享(共享端)
    isScreenShare() {
      return ["start", "pause"].includes(this.screenShareStatus);
    },
  },
  mounted() {
    // 使用 capture 模式确保事件能被正确捕获
    document.addEventListener("keydown", this.shortcutKey, true);
    if (window.ipcRenderer) {
      window.ipcRenderer.on("shortcut-mic-toggle", this.handleMicClick);
    }
    // 监听打开麦克风 popover 的事件
    EventBus.$on("openMicPopover", () => {
      console.log("openMicPopover");
      setTimeout(() => {
        this.popoverVisible = false;
        this.handleClickMore();
      }, 200);
    });

    // 添加全局点击监听，点击外部关闭 popover
    document.addEventListener("click", this.handleDocumentClick);
  },
  beforeDestroy() {
    // 移除事件监听器时也要指定 capture 模式
    document.removeEventListener("keydown", this.shortcutKey, true);
    document.removeEventListener("click", this.handleDocumentClick);
    if (window.ipcRenderer) {
      window.ipcRenderer.removeListener(
        "shortcut-mic-toggle",
        this.handleMicClick
      );
    }
    EventBus.$off("openMicPopover");
  },
  methods: {
    ...mapMutations("live", [
      "setMicrophoneList",
      "setSpeakerList",
      "setShowSet",
      "setCurrentSpeakerDevice",
      "setCurrentMicDevice",
      "setDeviceSwtich",
    ]),
    handleMicClick: debounce(
      function () {
        if (this.isMicLoading) {
          return;
        }
        this.isMicLoading = true;
        uploadLiveLog({
          message:
            "用户点击" + (!this.showCloseIcon ? "关闭" : "打开") + "麦克风",
          extra: {
            component: "MicSelector",
            action: "click_mic_toggle",
            currentAudioState: this.userStatus.audio_state,
            isUpStage: this.userStatus.is_up_stage,
          },
        });
        const isMute = !this.showCloseIcon;
        console.log("isMute_", isMute);
        this.handleSelfMic(isMute)
          .then(() => {
            this.isMicLoading = false;
          })
          .catch((error) => {
            this.isMicLoading = false;
            console.error("操作自己麦克风失败:", error);
          });
      },
      300,
      true
    ),
    // 选择麦克风
    handleSelectMic: debounce(
      function (device) {
        this.popoverVisible = false;
        if (this.isSelectMicLoading) {
          return;
        }
        this.isSelectMicLoading = true;
        uploadLiveLog({
          message: "用户点击麦克风切换",
          extra: {
            component: "MicSelector",
            action: "click_select_mic",
            deviceId: device.deviceId,
            deviceName: device.deviceName,
            isDisabled: device.deviceId === "disabled",
          },
        });

        if (device.deviceId === "disabled") {
          this.handleSelfMic(true)
            .then(() => {
              this.isSelectMicLoading = false;
              this.setDeviceSwtich({
                microphone: "off",
              });
            })
            .catch((error) => {
              this.isSelectMicLoading = false;
              console.error("禁用自己麦克风失败:", error);
            });
        } else {
          // if (this.userStatus.is_up_stage !== 1) {
          //   this.$darkMessage.error("上台后，才能开启音视频功能");
          //   return;
          // }
          // if (!this.audioPermission) {
          //   this.$darkMessage.error("打开系统权限后重试");
          //   return;
          // }
          // // 开启麦克风
          // if (this.userStatus.is_send_audio !== 1) {
          //   // 没有权限开启
          //   this.$darkMessage.error("您被老师禁止打开麦克风");
          //   return;
          // }

          this.handleSelfMic(false)
            .then(() => {
              this.setCurrentMicDevice(device);
              this.isSelectMicLoading = false;
              this.setDeviceSwtich({
                microphone: "on",
              });
            })
            .catch((error) => {
              this.isSelectMicLoading = false;
              console.error("切换自己麦克风失败:", error);
            });
        }
      },
      300,
      true
    ),
    // 选择扬声器
    handleSelectSpeaker: debounce(
      function (device) {
        uploadLiveLog({
          message: "用户点击扬声器切换",
          extra: {
            component: "MicSelector",
            action: "click_select_speaker",
            deviceId: device.deviceId,
            deviceName: device.deviceName,
            isDisabled: device.deviceId === "disabled",
          },
        });

        if (device.deviceId === "disabled") {
          this.setDeviceSwtich({
            speaker: "off",
          });
        } else {
          this.setDeviceSwtich({
            speaker: "on",
          });
          this.setCurrentSpeakerDevice(device);
        }

        this.popoverVisible = false;
      },
      300,
      true
    ),
    handleSetting() {
      uploadLiveLog({
        message: "用户点击扬声器设置",
        extra: {
          component: "MicSelector",
          action: "click_mic_setting",
        },
      });

      // 打开设置弹窗或页面
      this.setShowSet({
        visible: true,
        initActive: "audio",
      });
      this.popoverVisible = false;
    },
    handleClickMore() {
      uploadLiveLog({
        message: "用户点击扬声器展开选项",
        extra: {
          component: "MicSelector",
          action: "click_mic_more",
        },
      });

      // ⭐ 切换 popover 显示/隐藏（trigger="manual" 需要手动控制）
      this.popoverVisible = !this.popoverVisible;

      // 如果是打开 popover，获取设备列表
      if (this.popoverVisible && this.liveService) {
        this.liveService.getMicrophoneList().then((list) => {
          console.log("麦克分——————", list);
          this.setMicrophoneList(list.filter((item) => item.deviceId) || []);
        });
        this.liveService.getSpeakerDevicesList().then((list) => {
          console.log("扬声器列表", list);

          this.setSpeakerList(list || []);
        });
      }
    },
    onHidePopover() {
      this.popoverVisible = false;
    },
    onShowPopover() {
      this.popoverVisible = true;
    },
    onAfterLeave() {
      // popover 关闭动画完成后的回调
      console.log("popover 已关闭");
    },
    handleDocumentClick(event) {
      // 点击外部关闭 popover
      if (!this.popoverVisible) {
        return;
      }

      // 检查点击是否在 popover 或 reference 元素内
      const popoverEl = document.querySelector(".mic-popover");
      const referenceEl = this.$el;

      if (
        popoverEl &&
        !popoverEl.contains(event.target) &&
        referenceEl &&
        !referenceEl.contains(event.target)
      ) {
        // 点击在外部，关闭 popover
        this.popoverVisible = false;
      }
    },
    shortcutKey(event) {
      // 检查 Command+Shift+D (Mac) 或 Ctrl+Shift+D (Windows/Linux)
      // 兼容不同浏览器的按键检测

      if (
        (event.metaKey || event.ctrlKey) &&
        event.shiftKey &&
        (event.key === "d" || event.key === "D")
      ) {
        event.preventDefault(); // 阻止默认行为
        this.handleMicClick();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.mic-selector {
  display: flex;
  align-items: center;
  background: transparent;
}
.mic-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  outline: none;
  border-radius: 8px;
  // padding: 4px 8px 0 8px;
}
.mic-icon {
  width: 20px;
  height: 20px;
  // margin-bottom: 2px;
}
.mic-label {
  font-weight: 400;
  font-size: 11px;
  color: #fff;
  margin-top: 1px;
}
.arrow-btn {
  width: 16px;
  height: 42px;
  border-radius: 6px;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 10px;
  margin-left: 3px;
  &:hover {
    background: #27272a;
  }
}
.arrow-icon {
  font-size: 12px;
  color: #c9cdd4;
  display: flex;
  align-items: center;
  justify-content: center;
}

.popover-content {
}
.group-title {
  font-weight: 400;
  font-size: 12px;
  color: #86909c;
}
.device-list {
  list-style: none;
  // margin: 0 0 8px 0;
  // padding: 0 16px;
}
.device-item {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  height: 22px;
  font-weight: 400;
  font-size: 14px;
  color: #fff;
  cursor: pointer;
  overflow: hidden;
  // transition: background 0.2s;
  margin-top: 12px;
  .label {
    font-size: 14px !important;
  }
  &:hover:not(.disabled),
  &.selected {
    // background: #232329;
  }
  &.selected {
    // font-weight: bold;
    // color: #f54c46;
  }
  &.disabled {
    // color: #666;
    // cursor: not-allowed;
    // background: none;
  }
}
.checkmark {
  width: 22px;
  min-width: 22px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  img {
    width: 18px !important;
    height: 18px !important;
  }
}
.divider {
  height: 1px;
  background: #27272a;
  border-radius: 0px 0px 0px 0px;

  margin: 12px 0;
}
.bottom-setting {
  display: flex;
  align-items: center;
  cursor: pointer;
  color: #fff;
  font-size: 14px;
  border-radius: 6px;
  outline: none;
}
.setting-dash {
  width: 16px;
  height: 16px;
  border: 1px dashed #fff;
  border-radius: 4px;
  margin-right: 8px;
  display: inline-block;
}
.setting-label {
  font-size: 15px;
}
</style>
<style>
.mic-popover {
  width: 170px;
  background: #18181b;
  box-shadow: 0px 8px 60px 2px rgba(0, 0, 0, 0.8);
  border-radius: 12px 12px 12px 12px;
  border: 1px solid #27272a;
  padding: 12px;
}
</style>
